import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './firebase';
import { generateToken, saveProject, fetchProjects, deleteProject as dbDelete } from './services/db';
import AuthPage from './components/AuthPage';
import Dashboard from './components/Dashboard';
import ProjectEditor from './components/ProjectEditor';
import ClientPreview from './components/ClientPreview';
import ClientShareView from './components/ClientShareView';
import LandingPage from './components/LandingPage';
import SuccessPage from './components/SuccessPage';
import PaywallPage from './components/PaywallPage';
import SettingsPage from './components/SettingsPage';
import AIStudio from './components/AIStudio';
import AgencyTools from './components/AgencyTools';

// ─── Routing ──────────────────────────────────────────────────────────────────
const getShareToken = () => {
  const m = window.location.pathname.match(/^\/s\/([a-z0-9]+)$/i);
  return m ? m[1] : null;
};
const isSuccessPage = () => window.location.pathname === '/success';

const makeProject = (title = 'Unbenanntes Projekt', uid) => ({
  shareToken: generateToken(),
  ownerId: uid,
  title,
  createdAt: new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' }),
  status: 'Entwurf',
  blocks: [],
  driveLink: '',
});

export default function App() {
  const shareToken = getShareToken();
  if (shareToken) return <ClientShareView shareToken={shareToken} />;
  if (isSuccessPage()) return <SuccessPage onContinue={() => { window.history.pushState({}, '', '/'); window.location.reload(); }} />;
  return <AuthedApp />;
}

function AuthedApp() {
  const [user, setUser]               = useState(undefined);
  const [showAuth, setShowAuth]       = useState(false);
  const [subStatus, setSubStatus]     = useState('checking'); // 'checking' | 'active' | 'none'
  const [planTier, setPlanTier]       = useState('standard'); // 'standard' | 'agency'
  const [projects, setProjects]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [saveStatus, setSaveStatus]   = useState('idle');
  const [activeToken, setActiveToken] = useState(null);
  const [mode, setMode]               = useState('dashboard');
  const saveTimer                     = useRef(null);
  const statusTimer                   = useRef(null);

  useEffect(() => {
    return onAuthStateChanged(auth, u => setUser(u ?? null));
  }, []);

  // Check Stripe subscription whenever user changes
  useEffect(() => {
    if (!user) { setSubStatus('none'); return; }
    setSubStatus('checking');
    fetch('/.netlify/functions/check-subscription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: user.email }),
    })
      .then(r => r.json())
      .then(d => {
        setSubStatus(d.active ? 'active' : 'none');
        if (d.active && d.planTier) setPlanTier(d.planTier);
      })
      .catch(() => setSubStatus('active')); // fail open – don't block on network error
  }, [user]);

  const loadProjects = useCallback(async (uid) => {
    setLoading(true);
    try {
      const docs = await fetchProjects(uid);
      setProjects(docs.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0)));
    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    setShowAuth(false);
    loadProjects(user.uid);
  }, [user, loadProjects]);

  const activeProject = projects.find(p => p.shareToken === activeToken);

  const triggerSave = useCallback(async (updated) => {
    setSaveStatus('saving');
    clearTimeout(statusTimer.current);
    try {
      await saveProject(updated);
      setSaveStatus('saved');
    } catch (e) {
      console.error('Save error:', e);
      setSaveStatus('error');
    }
    statusTimer.current = setTimeout(() => setSaveStatus('idle'), 2500);
  }, []);

  const scheduleSave = useCallback((updated) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => triggerSave(updated), 800);
  }, [triggerSave]);

  const updateProject = useCallback((updated, immediate = false) => {
    setProjects(ps => ps.map(p => p.shareToken === updated.shareToken ? updated : p));
    if (immediate) { clearTimeout(saveTimer.current); triggerSave(updated); }
    else scheduleSave(updated);
  }, [scheduleSave, triggerSave]);

  const addProject = async (title) => {
    const p = makeProject(title, user.uid);
    setProjects(ps => [p, ...ps]);
    await saveProject(p);
    setActiveToken(p.shareToken);
    setMode('editor');
  };

  const deleteProject = async (token) => {
    setProjects(ps => ps.filter(p => p.shareToken !== token));
    await dbDelete(token);
    if (activeToken === token) { setActiveToken(null); setMode('dashboard'); }
  };

  const openProject    = (token) => { setActiveToken(token); setMode('editor'); };
  const goToDashboard  = () => { setActiveToken(null); setMode('dashboard'); };
  const goToSettings   = () => { setActiveToken(null); setMode('settings'); };
  const goToAgency     = () => { setActiveToken(null); setMode('agency'); };

  // ── Loading auth or subscription check ──
  if (user === undefined || (user && subStatus === 'checking')) return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-3">
      <svg className="animate-spin w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
      </svg>
      {user && <p className="text-xs text-gray-400">Abo wird geprüft…</p>}
    </div>
  );

  // ── Landing page (not logged in) ──
  if (!user && !showAuth) return <LandingPage onLogin={() => setShowAuth(true)} />;

  // ── Paywall (logged in but no subscription) ──
  if (user && subStatus === 'none') return <PaywallPage user={user} onTestBypass={(tier) => { setPlanTier(tier || 'standard'); setSubStatus('active'); if (tier === 'agency') setMode('agency'); }} />;

  // ── Auth page ──
  if (!user && showAuth) return (
    <div>
      <button
        onClick={() => setShowAuth(false)}
        className="fixed top-4 left-4 z-50 text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M10 6H2M5 3L2 6L5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
        Zurück
      </button>
      <AuthPage />
    </div>
  );

  // ── App (logged in) ──
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b border-gray-100 px-6 flex items-center justify-between h-14 sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <button onClick={goToDashboard} className="flex items-center gap-2 hover:opacity-75 transition-opacity">
            <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 2L13 8L3 14V2Z" fill="white"/></svg>
            </div>
            <span className="font-semibold text-gray-900 text-sm tracking-tight">FrameIt</span>
          </button>
          {activeProject && (
            <>
              <span className="text-gray-200 select-none">/</span>
              <span className="text-sm text-gray-500 font-medium truncate max-w-xs">{activeProject.title}</span>
            </>
          )}
        </div>

        {activeProject && (
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button onClick={() => setMode('editor')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${mode === 'editor' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Planung</button>
            <button onClick={() => setMode('preview')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${mode === 'preview' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Kunden-Preview</button>
            <button onClick={() => setMode('ai')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${mode === 'ai' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="6" r="2" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M6 1v1M6 10v1M1 6h1M10 6h1M2.5 2.5l.7.7M8.8 8.8l.7.7M2.5 9.5l.7-.7M8.8 3.2l.7-.7" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
              </svg>
              KI-Studio
            </button>
          </div>
        )}

        <div className="flex items-center gap-3">
          {saveStatus !== 'idle' && (
            <span className={`text-xs font-medium flex items-center gap-1.5 ${saveStatus === 'saving' ? 'text-gray-400' : saveStatus === 'saved' ? 'text-emerald-600' : 'text-red-500'}`}>
              {saveStatus === 'saving' && <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
              {saveStatus === 'saving' && 'Speichern…'}
              {saveStatus === 'saved'  && (
                <><svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> Gespeichert</>
              )}
              {saveStatus === 'error'  && (
                <><svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M6 2v5M6 9v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg> Fehler</>
              )}
            </span>
          )}
          {activeProject && mode === 'editor' && (
            <button onClick={() => setMode('preview')} className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-all">
              <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M8 3C4 3 1 8 1 8s3 5 7 5 7-5 7-5-3-5-7-5z" stroke="currentColor" strokeWidth="1.4"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4"/></svg>
              Vorschau
            </button>
          )}
          <div className="relative group">
            <button>
              {user.photoURL
                ? <img src={user.photoURL} alt="" className="w-7 h-7 rounded-full object-cover" />
                : <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-semibold">{(user.displayName || user.email || 'U')[0].toUpperCase()}</div>
              }
            </button>
            <div className="absolute right-0 top-9 w-52 bg-white rounded-xl border border-gray-100 shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <div className="px-3 py-2.5 border-b border-gray-50">
                <p className="text-xs font-semibold text-gray-800 truncate">{user.displayName || 'Kein Name'}</p>
                <p className="text-xs text-gray-400 truncate">{user.email}</p>
              </div>
              {planTier === 'agency' && (
                <button onClick={goToAgency} className="w-full text-left px-3 py-2 text-xs text-purple-600 hover:bg-purple-50 flex items-center gap-2">
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M6 1l1.5 3H11l-2.5 2 1 3L6 7.5 2.5 9l1-3L1 4h3.5L6 1z" fill="currentColor"/></svg>
                  Agentur-Tools
                </button>
              )}
              <button onClick={goToSettings} className="w-full text-left px-3 py-2 text-xs text-gray-500 hover:bg-gray-50 flex items-center gap-2">
                <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 1a6 6 0 100 12A6 6 0 007 1zM7 5v4M7 9h.01" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
                Einstellungen
              </button>
              <button onClick={() => loadProjects(user.uid)} className="w-full text-left px-3 py-2 text-xs text-gray-500 hover:bg-gray-50 flex items-center gap-2">
                <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M13 7A6 6 0 112 4.5M2 1v4h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Neu laden
              </button>
              <button onClick={() => signOut(auth)} className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-50 flex items-center gap-2">
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M5 2H2a1 1 0 00-1 1v8a1 1 0 001 1h3M9 10l3-3-3-3M13 7H5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Abmelden
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-hidden">
        {mode === 'dashboard' && <Dashboard user={user} projects={projects} loading={loading} onOpen={openProject} onAdd={(p) => { setProjects(ps => [p, ...ps]); setActiveToken(p.shareToken); setMode('editor'); }} onDelete={deleteProject} planTier={planTier} onAgency={goToAgency} />}
        {mode === 'editor'    && activeProject && <ProjectEditor project={activeProject} onChange={updateProject} />}
        {mode === 'preview'   && activeProject && <ClientPreview project={activeProject} onChange={updateProject} />}
        {mode === 'ai'        && activeProject && <AIStudio project={activeProject} onChange={updateProject} />}
        {mode === 'settings'  && <SettingsPage user={user} onBack={goToDashboard} planTier={planTier} />}
        {mode === 'agency'    && planTier === 'agency' && <AgencyTools user={user} onBack={goToDashboard} />}
      </main>
    </div>
  );
}
